import React, { useCallback, useEffect, useState } from "react";
import { Text, View } from "react-native";
import tw from "twrnc";

import { getEventEmbedPages, updateEventEmbedPages } from "../http/events";
import { notify } from "./AppToast";
import Button, { ButtonRow } from "@/components/settings/Button";
import { Hint } from "@/components/settings/Field";
import { SettingsSection } from "@/components/settings/SettingsPage";
import { colors, radius, text } from "@/components/settings/theme";
import { t } from "@/localization";

// Guests who watch on the broadcaster's site get email links back to the page
// they watched on: automatically on the Branding website or the old Embed
// webpage URL's site, other pages once the broadcaster confirms them here.
// Pages come from viewers' browsers, so unconfirmed ones are never linked
// (eventlive-cdk/lambda/linkBrandingUtils.ts trustedEmbedPage).
const EmbedEmailLinks = ({ slug }) => {
  const [pages, setPages] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let current = true;
    getEventEmbedPages(slug)
      .then((event) => current && setPages(event))
      .catch((err) => console.error(err));
    return () => {
      current = false;
    };
  }, [slug]);

  const save = useCallback(
    async (details) => {
      if (busy) return;
      setBusy(true);
      try {
        setPages(await updateEventEmbedPages({ slug, details }));
      } catch (err) {
        console.error(err);
        notify.error(
          t("Couldn't save that"),
          t("Check your connection and try again.")
        );
      } finally {
        setBusy(false);
      }
    },
    [slug, busy]
  );

  if (!pages) return null;

  const seen = pages.embed_pages_seen || [];
  const confirmed = pages.embed_pages_confirmed || [];

  // One page per card: the address, then its actions.
  const row = (page, actions) => (
    <View
      key={page}
      style={[
        tw`px-3 py-2.5 mb-2`,
        {
          borderWidth: 1,
          borderColor: colors.divider,
          borderRadius: radius.control,
          backgroundColor: colors.surface,
        },
      ]}
    >
      <Text style={[text.body, tw`mb-1.5`]} selectable numberOfLines={2}>
        {page}
      </Text>
      <ButtonRow align="start">
        {actions.map(({ text: label, details, variant = "secondary" }) => (
          <Button
            key={label}
            text={label}
            variant={variant}
            size="sm"
            fullWidth={false}
            disabled={busy}
            onPress={() => save(details)}
          />
        ))}
      </ButtonRow>
    </View>
  );

  const label = (title) => (
    <Text style={[text.label, tw`mt-2 mb-1.5`]}>{title}</Text>
  );

  return (
    <SettingsSection
      title={t("Email links")}
      help="embed.emailLinks"
      description={t(
        "Guests who watch on your website get emails that link back to the page they watched on."
      )}
    >
      {!seen.length && !confirmed.length && !pages.embedlink && (
        <Hint style={tw`mt-0`}>
          {t("Pages where guests watch your embed will show up here.")}
        </Hint>
      )}

      {seen.length > 0 && (
        <>
          {label(t("Seen on"))}
          {seen.map((page) =>
            row(page, [
              {
                text: t("Use for email links"),
                details: { embed_page: page, embed_page_action: "confirm" },
              },
              {
                text: t("Ignore"),
                variant: "tertiary",
                details: { embed_page: page, embed_page_action: "ignore" },
              },
            ])
          )}
        </>
      )}

      {confirmed.length > 0 && (
        <>
          {label(t("Used for email links"))}
          {confirmed.map((page) =>
            row(page, [
              {
                text: t("Stop using"),
                variant: "tertiary",
                details: { embed_page: page, embed_page_action: "forget" },
              },
            ])
          )}
        </>
      )}

      {!!pages.embedlink && (
        <>
          {label(t("Other guests"))}
          {row(pages.embedlink, [
            {
              text: t("Remove"),
              variant: "danger-text",
              details: { embedlink: null },
            },
          ])}
          <Hint style={tw`mt-0`}>
            {t(
              "Your Embed webpage URL from before. Remove it to send everyone else to evt.live."
            )}
          </Hint>
        </>
      )}
    </SettingsSection>
  );
};

export default EmbedEmailLinks;
